import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { PANEL_WIDTH, RAIL_WIDTH } from './NavLayout.styled';
import {
  closeBtnBg,
  closeBtnBgHover,
  closeBtnBorder,
  closeBtnBorderHover,
  closeBtnColor,
  mutedItemHoverBg,
  mutedItemHoverText,
  mutedItemText,
  panelBg,
  panelBorder,
  panelItemHoverBg as itemHoverBg,
  panelItemText as itemText,
  scrollbarThumb,
  scrollbarThumbHover,
  sectionLabel,
  subItemConnector,
  subItemText,
  subtleBorder,
  titleText,
  itemHoverText,
} from './palette';

export const PanelWrapper = styled.div`
  width: ${(props) => (props.isOpen ? `${PANEL_WIDTH}px` : '0px')};
  overflow: hidden;
  transition: width 300ms cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  position: fixed;
  top: 0;
  left: ${RAIL_WIDTH}px;
  height: 100vh;
  z-index: 9;
`;

export const Panel = styled.aside`
  width: ${PANEL_WIDTH}px;
  height: 100%;
  background: ${panelBg};
  border-right: 1px solid ${panelBorder};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const PanelBrand = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${panelBorder};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const PanelBrandLogo = styled.img`
  height: 32px;
  width: auto;
  display: block;
`;

export const PanelCloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${closeBtnBg};
  border: 1px solid ${closeBtnBorder};
  color: ${closeBtnColor};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  flex-shrink: 0;
  position: relative;
  padding: 0;

  &:hover {
    background: ${closeBtnBgHover};
    color: ${itemHoverText};
    border-color: ${closeBtnBorderHover};
  }
`;

export const CollapseIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  width: 20px;
  height: 20px;
  position: relative;
`;

export const CollapseBar = styled.span`
  width: 16px;
  height: 2px;
  background: currentColor;
  border-radius: 1px;
  transition: all 250ms ease-in-out;
  position: absolute;

  &:nth-child(1) {
    top: 4px;
  }
  &:nth-child(2) {
    top: 9px;
    width: 12px;
  }
  &:nth-child(3) {
    top: 14px;
    width: 8px;
  }
`;

export const CollapseArrow = styled.span`
  position: absolute;
  right: -1px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-right: 5px solid currentColor;
  transition: all 250ms ease-in-out;
`;

export const PanelAccountRow = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${panelBorder};
  flex-shrink: 0;
`;

export const PanelHeader = styled.div`
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${panelBorder};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

export const PanelTitle = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: ${titleText};
  font-family: ${(props) => props.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${scrollbarThumb};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${scrollbarThumbHover};
  }
`;

export const PanelSection = styled.div`
  padding: 0 0 4px;
`;

export const PanelSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${sectionLabel};
  text-transform: uppercase;
  letter-spacing: 1.2px;
  padding: 14px 16px 8px;
  white-space: nowrap;
  border-bottom: 1px solid ${subtleBorder};
  margin-bottom: 2px;
  font-family: ${(props) => props.theme.fonts.primary};
`;

export const PanelItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  font-size: 15px;
  font-weight: 500;
  color: ${itemText};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  position: relative;
  user-select: none;
  white-space: nowrap;
  margin: 0 8px;
  border-radius: 8px;
  text-decoration: none;
  font-family: ${(props) => props.theme.fonts.primary};

  /* Orange left bar indicator */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 0;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 4px 0 0 4px;
    transition: height 150ms ease-in-out;
  }

  &:hover {
    color: ${itemHoverText};
    background: ${itemHoverBg};
  }
  &:hover::before {
    height: 22px;
  }

  &.active {
    color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.primaryOpacity(0.12)};
    font-weight: 600;
  }
  &.active::before {
    height: 100%;
    background: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 8px ${(props) => props.theme.colors.primaryOpacity(0.3)};
  }
`;

export const PanelItemExternal = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  font-size: 15px;
  font-weight: 500;
  color: ${itemText};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  position: relative;
  user-select: none;
  white-space: nowrap;
  margin: 0 8px;
  border-radius: 8px;
  text-decoration: none;
  font-family: ${(props) => props.theme.fonts.primary};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 0;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 4px 0 0 4px;
    transition: height 150ms ease-in-out;
  }

  &:hover {
    color: ${itemHoverText};
    background: ${itemHoverBg};
  }
  &:hover::before {
    height: 22px;
  }
`;

export const PanelItemButton = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  font-size: 15px;
  font-weight: 500;
  color: ${mutedItemText};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  position: relative;
  user-select: none;
  white-space: nowrap;
  margin: 0 8px;
  border-radius: 8px;
  font-family: ${(props) => props.theme.fonts.primary};

  &:hover {
    color: ${mutedItemHoverText};
    background: ${mutedItemHoverBg};
  }
`;

/* Expandable section toggle */
export const ExpandToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  font-size: 11px;
  font-weight: 700;
  color: ${sectionLabel};
  text-transform: uppercase;
  letter-spacing: 1.2px;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  user-select: none;
  white-space: nowrap;
  font-family: ${(props) => props.theme.fonts.primary};

  &:hover {
    color: ${itemHoverText};
    background: ${itemHoverBg};
  }
`;

export const ExpandArrow = styled.span`
  margin-left: auto;
  transition: transform 150ms ease-in-out;
  color: ${sectionLabel};
  flex-shrink: 0;
  display: inline-flex;
  transform: ${(props) => (props.isExpanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const ExpandArrowIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: currentColor;
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

/* Sub-items with tree connector */
export const SubGroup = styled.div`
  position: relative;
  margin-left: 16px;
  padding-left: 14px;
  border-left: 1px solid ${subItemConnector};
  max-height: ${(props) => (props.isExpanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 300ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 250ms ease-in-out;
  opacity: ${(props) => (props.isExpanded ? 1 : 0)};
`;

export const SubItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px 9px 10px;
  font-size: 14px;
  font-weight: 400;
  color: ${subItemText};
  cursor: pointer;
  transition: all 150ms ease-in-out;
  border-radius: 0 4px 4px 0;
  position: relative;
  user-select: none;
  white-space: nowrap;
  text-decoration: none;
  font-family: ${(props) => props.theme.fonts.primary};

  /* Tree connector line */
  &::before {
    content: '';
    position: absolute;
    left: -14px;
    top: 50%;
    width: 10px;
    height: 1px;
    background: ${subItemConnector};
  }

  &:hover {
    color: ${itemHoverText};
    background: ${itemHoverBg};
  }

  &.active {
    color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.primaryOpacity(0.08)};
    font-weight: 600;
  }
`;

export const SubDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
  flex-shrink: 0;
`;

export const PanelExpandButton = styled.button`
  position: fixed;
  left: ${RAIL_WIDTH}px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 48px;
  background: ${panelBg};
  border: 1px solid ${panelBorder};
  border-left: none;
  border-radius: 0 8px 8px 0;
  color: ${closeBtnColor};
  cursor: pointer;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease-in-out;
  padding: 0;

  &:hover {
    background: ${closeBtnBgHover};
    color: ${(props) => props.theme.colors.primary};
    width: 32px;
    border-color: ${(props) => props.theme.colors.primaryOpacity(0.12)};
  }
`;
